import {defineStore} from 'pinia'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {useSocketStore} from '@toolify/client/src/stores/SocketStore/useSocketStore'
import {
  INotificationResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/INotificationResponseInstance'
import {NotificationType} from '@toolify/server/src/models/mongoose/NotificationModel/enum/NotificationType'
import {IResponseEntity} from '@toolify/server/src/services/EntityService/types/IResponseEntity'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {$ref, $$, $computed} from '@vue-macros/reactivity-transform/macros'
import {useNativeAppStore} from '@toolify/client/src/stores/AppTitlebarStore/useNativeAppStore'
import {isElectron} from '@toolify/client/src/modules/electron/singletons/isElectron'
import {
  NotificationTypeStrategyFactory,
} from '@toolify/client/src/stores/NotificationStore/factories/NotificationTypeStrategyFactory'
import {useNotificationApiStore} from '@toolify/client/src/stores/api/NotificationApiStore/useNotificationApiStore'
import {
  GLOBAL_NOTIFICATION_COUNTS_SCOPE_KEY,
  useNotificationCountsStore,
} from '@toolify/client/src/stores/NotificationCountsStore/useNotificationCountsStore'

import {mapNotificationResponseInstance} from '@toolify/client/src/modules/api/singletons/mapNotificationResponseInstance'
import {IToJson} from '@toolify/client/src/types/IToJson'
import {computed, watch} from 'vue'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {
  IFileVariantResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IFileVariantResponseInstance'
import {
  IImageToImageFileConversionTypeStrategyInterfaces,
} from '@toolify/server/src/services/FileVariantService/strategies/FileConversionTypeStrategies/ImageToImageFileConversionTypeStrategy/types/IImageToImageFileConversionTypeStrategyInterfaces'
import {FileVariantType} from '@toolify/server/src/models/mongoose/FileVariantModel/enum/FileVariantType'
import {getEntityNameByEntity} from '@toolify/client/src/modules/entities/functions/getEntityNameByEntity'
import {useWorkspaceStore} from '../WorkspaceStore/useWorkspaceStore'
import {useBrowserTabs} from '@toolify/client/src/modules/broadcast/composables/useBrowserTabs'
import {useMobileNotificationsStore} from '@toolify/client/src/stores/MobileNotificationsStore/useMobileNotificationsStore'
import {useCurrentAppPlatform} from '../../modules/platform/composables/useCurrentAppPlatform'
import {AppPlatformType} from '@toolify/server/src/models/mongoose/SessionModel/enum/AppPlatformType'
import {useSocketListener} from '@toolify/client/src/composables/useSocketListener'
import {
  InotificationReadEventPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/INotificationReadEmitPayload'
import {INotificationUpdatedEmitPayload} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/INotificationUpdatedEmitPayload'
import {NotificationFilterType} from '@toolify/server/src/controllers/NotificationController/enum/NotificationFilterType'
import {useDocumentVisibility, useWindowFocus} from '@vueuse/core'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import {i18n} from '@toolify/client/src/setup/i18n'
import {
  IBusyDeferredNotificationsDeliveredEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IBusyDeferredNotificationsDeliveredEmitPayload'
import {
  getNotificationSoundUrl,
} from '@toolify/client/src/stores/NotificationStore/functions/getNotificationSoundUrl'
import {useOperatingSystem} from '@toolify/client/src/modules/app/composables/useOperatingSystem'
import {OperatingSystemType} from '@toolify/server/src/controllers/NativeAppController/enum/OperatingSystemType'
import {useOpenConversations} from '@toolify/client/src/modules/chat/composables/useOpenConversations'
import {useLocalMessageStore} from '@toolify/client/src/stores/LocalMessageStore/useLocalMessageStore'
import {MessageAnnouncementChannelType} from '@toolify/client/src/modules/notifications/enum/MessageAnnouncementChannelType'
import {formatNotificationCount} from '@toolify/client/src/modules/notifications/functions/formatNotificationCount'

const messageNotificationTypes = new Set<NotificationType>([
  NotificationType.NewMessage,
  NotificationType.MentionedInMessage,
  NotificationType.RepliedToMyMessage,
  NotificationType.AiApprovalRequested,
  NotificationType.AiRunFinished,
])

const aiAgentNotificationTypes = new Set<NotificationType>([
  NotificationType.AiApprovalRequested,
  NotificationType.AiRunFinished,
])

function getNotificationConversation(notification: INotificationResponseInstance<NotificationType>) {
  if(!messageNotificationTypes.has(notification.type)) {
    return null
  }
  const {conversationType, conversationPayload} = (notification as INotificationResponseInstance<NotificationType.NewMessage>).payload
  if(!conversationType || !conversationPayload) {
    return null
  }
  return {
    conversationType,
    conversationPayload,
  }
}

type IPopupNotificationTimeoutState = {
  remainingMs: number
  startedAt: number | null
  timeout: ReturnType<typeof setTimeout> | null
  // when the popup joined the floating stack; orders it among the local messages
  queuedAt: number
}

const POPUP_NOTIFICATION_TIMEOUT_MS = 6000
const NOTIFICATION_SOUND_MIN_INTERVAL_MS = 1500
const UNSEEN_COUNT_ANNOUNCEMENT_DELAY_MS = 2000
let lastNotificationSoundPlayedAt = 0

export const useNotificationStore = defineStore('notification', () => {
  const socketStore = useSocketStore()
  const nativeAppStore = useNativeAppStore()
  const notificationApiStore = useNotificationApiStore()
  const notificationCountsStore = useNotificationCountsStore()
  const authStore = useAuthStore()
  const {isPrimaryTab} = useBrowserTabs()
  const messages = useMessages()
  const mobileNotificationsStore = useMobileNotificationsStore()
  const localMessageStore = useLocalMessageStore()
  const isWindowFocused = useWindowFocus()
  const documentVisibility = useDocumentVisibility()
  const {isConversationOpen} = useOpenConversations()
  let unseenCountAnnouncementTimeout: ReturnType<typeof setTimeout> | null = null

  const {currentAppPlatformType} = useCurrentAppPlatform()
  const {isAppPlatformMobile} = useCurrentAppPlatform()
  const {getOperatingSystem} = useOperatingSystem()

  let areNotificationsSupported = $ref(false)
  let hasBrowserPermission = $ref(false)
  let checkPermissionsInterval = $ref(null)
  // Counts are owned by the shared counts store; the global scope is registered in initialize()
  const unseenNotificationsCount = $computed(() => {
    return notificationCountsStore.getUnseenCount(GLOBAL_NOTIFICATION_COUNTS_SCOPE_KEY)
  })
  const unreadNotificationsCount = $computed(() => {
    return notificationCountsStore.getCounts(GLOBAL_NOTIFICATION_COUNTS_SCOPE_KEY)[NotificationFilterType.Unread] || 0
  })
  let popupNotifications = $ref<INotificationResponseInstance<NotificationType>[]>([])
  const popupNotificationTimeoutMap = new Map<string, IPopupNotificationTimeoutState>()
  const hoveredPopupNotificationIds = new Set<string>()
  // popups folded into the stack's "+N" pill must not expire unseen
  const collapsedPopupNotificationIds = new Set<string>()

  const hasPermission = computed(() => {
    if(currentAppPlatformType === AppPlatformType.Mobile) {
      return mobileNotificationsStore.hasPermission
    }
    return hasBrowserPermission
  })

  const shouldRunPopupTimers = computed(() => {
    if(shouldUseDesktopFloatingNotificationsWindow.value) {
      return true
    }
    return documentVisibility.value === 'visible'
  })

  const shouldUseDesktopFloatingNotificationsWindow = computed(() => {
    if(!isElectron()) {
      return false
    }
    return getOperatingSystem() === OperatingSystemType.Windows
  })

  function initialize() {
    socketStore.socket.on(GlobalSocketEventType.NotificationCreated, onNotificationCreated)
    socketStore.socket.on(GlobalSocketEventType.BusyDeferredNotificationsDelivered, onBusyDeferredNotificationsDelivered)
    // Keeps the global badge/Electron counts alive even when no bell component is mounted
    notificationCountsStore.registerScope()
    areNotificationsSupported = window && 'Notification' in window
    checkPermission()
    checkPermissionsInterval = setInterval(() => {
      checkPermission()
    }, 5000)
  }

  async function checkPermission() {
    if(currentAppPlatformType === AppPlatformType.Mobile) {
      hasBrowserPermission = false
      return
    }
    if(!areNotificationsSupported) {
      hasBrowserPermission = false
      return
    } else {
      if('permission' in Notification && Notification.permission === 'granted') {
        hasBrowserPermission = true
        return
      } else {
        hasBrowserPermission = false
        return
      }
    }
  }

  function dispose() {
    socketStore.socket.off(GlobalSocketEventType.NotificationCreated, onNotificationCreated)
    socketStore.socket.off(GlobalSocketEventType.BusyDeferredNotificationsDelivered, onBusyDeferredNotificationsDelivered)
    notificationCountsStore.unregisterScope(GLOBAL_NOTIFICATION_COUNTS_SCOPE_KEY)
    clearInterval(checkPermissionsInterval)
    clearUnseenCountAnnouncement()
    popupNotificationTimeoutMap.forEach(timeout => {
      if(timeout.timeout) {
        clearTimeout(timeout.timeout)
      }
    })
    popupNotificationTimeoutMap.clear()
  }

  function dismissPopupNotification(notificationId: string) {
    const timeout = popupNotificationTimeoutMap.get(notificationId)
    if(timeout?.timeout) {
      clearTimeout(timeout.timeout)
    }
    popupNotificationTimeoutMap.delete(notificationId)
    hoveredPopupNotificationIds.delete(notificationId)
    collapsedPopupNotificationIds.delete(notificationId)
    popupNotifications = popupNotifications.filter(notification => notification.id !== notificationId)
  }

  function pausePopupNotificationDismiss(notificationId: string) {
    const state = popupNotificationTimeoutMap.get(notificationId)
    if(!state || !state.timeout) {
      return
    }

    clearTimeout(state.timeout)
    state.timeout = null
    state.remainingMs = Math.max(0, state.remainingMs - (Date.now() - (state.startedAt ?? Date.now())))
    state.startedAt = null
  }

  function startPopupNotificationDismiss(notificationId: string) {
    const state = popupNotificationTimeoutMap.get(notificationId)
    if(
      !state
      || state.timeout
      || state.remainingMs <= 0
      || !shouldRunPopupTimers.value
      || hoveredPopupNotificationIds.has(notificationId)
      || collapsedPopupNotificationIds.has(notificationId)
    ) {
      return
    }

    state.startedAt = Date.now()
    state.timeout = setTimeout(() => {
      dismissPopupNotification(notificationId)
    }, state.remainingMs)
  }

  function updatePopupNotificationTimers() {
    popupNotificationTimeoutMap.forEach((_, notificationId) => {
      if(
        shouldRunPopupTimers.value
        && !hoveredPopupNotificationIds.has(notificationId)
        && !collapsedPopupNotificationIds.has(notificationId)
      ) {
        startPopupNotificationDismiss(notificationId)
        return
      }

      pausePopupNotificationDismiss(notificationId)
    })
  }

  function queuePopupNotification(notification: INotificationResponseInstance<NotificationType>) {
    const matchingNotification = popupNotifications.find(existingNotification => {
      if(existingNotification.id === notification.id) {
        return true
      }

      return !!existingNotification.aggregationKey
        && !!notification.aggregationKey
        && existingNotification.aggregationKey === notification.aggregationKey
        && existingNotification.type === notification.type
    })

    if(matchingNotification) {
      popupNotifications = popupNotifications.filter(existingNotification => {
        return existingNotification.id !== matchingNotification.id
      })
      dismissPopupNotification(matchingNotification.id)
    }

    const nextPopupNotifications = [...popupNotifications, notification].slice(-4)
    popupNotifications.forEach(existingNotification => {
      const isStillVisible = nextPopupNotifications.some(nextNotification => {
        return nextNotification.id === existingNotification.id
      })

      if(!isStillVisible) {
        dismissPopupNotification(existingNotification.id)
      }
    })

    // the timer state (with queuedAt) exists before the popup reaches the overlay's layout
    popupNotificationTimeoutMap.set(notification.id, {
      remainingMs: POPUP_NOTIFICATION_TIMEOUT_MS,
      startedAt: null,
      timeout: null,
      queuedAt: Date.now(),
    })
    popupNotifications = nextPopupNotifications
    startPopupNotificationDismiss(notification.id)
  }

  function getPopupNotificationQueuedAt(notificationId: string): number | null {
    return popupNotificationTimeoutMap.get(notificationId)?.queuedAt ?? null
  }

  // The popup keeps its remaining time while folded into the "+N" pill of the floating stack
  function setPopupNotificationCollapsed(notificationId: string, isCollapsed: boolean) {
    if(!popupNotificationTimeoutMap.has(notificationId)) {
      collapsedPopupNotificationIds.delete(notificationId)
      return
    }
    if(isCollapsed) {
      collapsedPopupNotificationIds.add(notificationId)
      pausePopupNotificationDismiss(notificationId)
      return
    }
    collapsedPopupNotificationIds.delete(notificationId)
    startPopupNotificationDismiss(notificationId)
  }

  function setPopupNotificationHovered(notificationId: string, isHovered: boolean) {
    const state = popupNotificationTimeoutMap.get(notificationId)
    if(!state) {
      return
    }

    if(state.timeout) {
      clearTimeout(state.timeout)
      state.timeout = null
    }
    state.startedAt = null
    state.remainingMs = POPUP_NOTIFICATION_TIMEOUT_MS

    if(isHovered) {
      hoveredPopupNotificationIds.add(notificationId)
      return
    }

    hoveredPopupNotificationIds.delete(notificationId)
    startPopupNotificationDismiss(notificationId)
  }

  function isNotificationConversationOnScreen(notification: INotificationResponseInstance<NotificationType>): boolean {
    const conversation = getNotificationConversation(notification)
    if(!conversation || documentVisibility.value !== 'visible') {
      return false
    }
    return isConversationOpen(conversation.conversationType, conversation.conversationPayload)
  }

  async function onNotificationCreated({notification, isQuiet, isBusySuppressed}: {
    notification: INotificationResponseInstance<NotificationType>
    isQuiet?: boolean
    isBusySuppressed?: boolean
  }) {
    const mappedNotification = mapNotificationResponseInstance(notification as unknown as IToJson<INotificationResponseInstance<NotificationType>>)

    // Both flags mean the same thing here: the counts store has already synced the list and the
    // badge, only the interruptive surfaces are skipped
    if(isQuiet || isBusySuppressed || isNotificationConversationOnScreen(mappedNotification)) {
      return
    }
    if(notificationCountsStore.isNotificationSeenLive(mappedNotification)) {
      return
    }
    scheduleUnseenCountAnnouncement()

    const authorEntity = notification.authorEntity
    type ImageFileVariant = IFileVariantResponseInstance<IImageToImageFileConversionTypeStrategyInterfaces['payload']>
    const authorAvatarUrl = $computed(() => {
      if(authorEntity.entityType !== EntityType.User) {
        return ''
      }
      return (authorEntity as IResponseEntity<EntityType.User>).payload?.completedAvatarFileVariants?.find(fileVariant => {
        if(fileVariant.type !== FileVariantType.Image) {
          return false
        }
        return (fileVariant as ImageFileVariant).payload.height === 64
          && (fileVariant as ImageFileVariant).payload.width === 64
      })?.url || ''
    })
    const authorNick = getEntityNameByEntity(authorEntity)
    const shouldHandleNotification = isPrimaryTab.value
    let shouldPlayNotificationSound = true
    const icon = authorAvatarUrl
    const notificationTypeStrategyFactory = new NotificationTypeStrategyFactory()
    const strategy = notificationTypeStrategyFactory.getStrategy(notification.type)
    const {body, title, sound} = strategy.getBrowserNotification(notification, authorNick)
    try {
      const isSoundAudible = aiAgentNotificationTypes.has(notification.type)
        || notification.resourcePath[0].resourceId === useWorkspaceStore().currentWorkspace.id
      if(isSoundAudible && shouldHandleNotification && sound) {
        const now = Date.now()
        if(now - lastNotificationSoundPlayedAt >= NOTIFICATION_SOUND_MIN_INTERVAL_MS) {
          lastNotificationSoundPlayedAt = now
          await new Audio(sound).play()
        }
        shouldPlayNotificationSound = false
      }
    } catch(e) {
      shouldPlayNotificationSound = false
    }

    if(!mappedNotification.isTransient && shouldHandleNotification && shouldRunPopupTimers.value) {
      queuePopupNotification(mappedNotification)
    }

    if(!areNotificationsSupported) {
      // eslint-disable-next-line no-console
      return console.log('Notifications unsupported')
    }
    if(!hasPermission.value) {
      // eslint-disable-next-line no-console
      return console.log('No permission to show notifications')
    }
    if(title && shouldHandleNotification && !shouldUseDesktopFloatingNotificationsWindow.value) {
      const browserNotification = new Notification(title, {
        body,
        icon,
        silent: !shouldPlayNotificationSound,
      })
    }
  }

  function clearUnseenCountAnnouncement() {
    if(unseenCountAnnouncementTimeout) {
      clearTimeout(unseenCountAnnouncementTimeout)
      unseenCountAnnouncementTimeout = null
    }
  }

  function scheduleUnseenCountAnnouncement() {
    clearUnseenCountAnnouncement()
    unseenCountAnnouncementTimeout = setTimeout(() => {
      unseenCountAnnouncementTimeout = null
      const count = unseenNotificationsCount
      if(!count || !isPrimaryTab.value || documentVisibility.value !== 'visible') {
        return
      }
      localMessageStore.announce(
        MessageAnnouncementChannelType.Polite,
        i18n.global.t('notifications.newNotificationsCount', {count: formatNotificationCount(count)}, count),
      )
    }, UNSEEN_COUNT_ANNOUNCEMENT_DELAY_MS)
  }

  // Raised once when the user leaves the Busy status: the rows are already in the list and counted,
  // so this only re-raises the interruptive surfaces, aggregated into a single summary
  async function onBusyDeferredNotificationsDelivered(payload: IBusyDeferredNotificationsDeliveredEmitPayload) {
    const count = payload?.count ?? 0
    if(count <= 0 || !isPrimaryTab.value) {
      return
    }

    const title = i18n.global.t('notifications.busyMode.deferredDeliveredTitle')
    const message = i18n.global.t('notifications.busyMode.deferredDelivered', {count})
    let shouldPlayNotificationSound = true
    try {
      await new Audio(getNotificationSoundUrl('new-notification.wav')).play()
      shouldPlayNotificationSound = false
    } catch(e) {
      void e
    }

    messages.info(message, {
      title,
      duration: POPUP_NOTIFICATION_TIMEOUT_MS,
    })

    // the desktop app raises the message above natively on its own while it is hidden (useNativeLocalMessages)
    if(!areNotificationsSupported || !hasPermission.value || isElectron()) {
      return
    }
    new Notification(title, {
      body: message,
      silent: !shouldPlayNotificationSound,
    })
  }

  async function requestPermission() {
    if (currentAppPlatformType === AppPlatformType.Mobile) {
      await mobileNotificationsStore.requestNotificationPermission()
      return
    }
    if(!hasBrowserPermission) {
      if (areNotificationsSupported) {
        const permission = await Notification.requestPermission()
        hasBrowserPermission = permission === 'granted'
      }
    }
  }

  if(isElectron()) {
    watch(
      () => ({
        unseen: unseenNotificationsCount,
        unread: unreadNotificationsCount,
      }),
      ({unseen, unread}) => {
        nativeAppStore.updateNotificationCount(unseen)
        nativeAppStore.updateUnreadCount(unread)
      },
      {
        immediate: true,
      },
    )
  }

  watch(() => {
    return authStore.currentEntity?.entityId
  }, (value, oldValue) => {
    if(value === oldValue) {
      return
    }
    if(oldValue) {
      clearUnseenCountAnnouncement()
      popupNotificationTimeoutMap.forEach(timeout => {
        if(timeout.timeout) {
          clearTimeout(timeout.timeout)
        }
      })
      popupNotificationTimeoutMap.clear()
      hoveredPopupNotificationIds.clear()
      collapsedPopupNotificationIds.clear()
      popupNotifications = []
    }
  })

  watch(shouldRunPopupTimers, () => {
    updatePopupNotificationTimers()
  })

  function updateNotificationReadState(notificationId: string, readAt: Date | null) {
    popupNotifications = popupNotifications.map(notification => {
      if(notification.id === notificationId) {
        return {
          ...notification,
          readAt,
        }
      }
      return notification
    })
  }

  async function markNotificationAsRead(notificationId: string) {
    await notificationApiStore.markNotificationAsRead(notificationId)
    updateNotificationReadState(notificationId, new Date())
  }

  async function markNotificationAsUnread(notificationId: string) {
    await notificationApiStore.markNotificationAsUnread(notificationId)
    updateNotificationReadState(notificationId, null)
  }

  useSocketListener(GlobalSocketEventType.NotificationRead, onNotificationRead)
  useSocketListener(GlobalSocketEventType.NotificationUpdated, onNotificationUpdated)

  function onNotificationRead(payload: InotificationReadEventPayload) {
    updateNotificationReadState(payload.notification.id, payload.notification.readAt)
  }

  function onNotificationUpdated({notification}: INotificationUpdatedEmitPayload) {
    const mappedNotification = mapNotificationResponseInstance(
      notification as unknown as IToJson<INotificationResponseInstance<NotificationType>>,
    )
    popupNotifications = popupNotifications.map(item => item.id === mappedNotification.id ? mappedNotification : item)
  }

  return $$({
    areNotificationsSupported,
    hasPermission,
    unseenNotificationsCount,
    popupNotifications,
    dismissPopupNotification,
    setPopupNotificationHovered,
    setPopupNotificationCollapsed,
    getPopupNotificationQueuedAt,
    initialize,
    dispose,
    requestPermission,
    markNotificationAsRead,
    markNotificationAsUnread,
  })
})
